import * as THREE from 'three';

// Small helpers for running fullscreen shader passes.

const triangle = (() => {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 2, 0, 0, 2], 2));
  return g;
})();

export const FULLSCREEN_VERT = /* glsl */ `
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

/** A single fullscreen-triangle pass with its own orthographic camera. */
export class FullscreenPass {
  readonly material: THREE.ShaderMaterial;
  private readonly mesh: THREE.Mesh;
  private readonly scene = new THREE.Scene();
  private static readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  constructor(fragmentShader: string, uniforms: Record<string, THREE.IUniform>, opts: Partial<THREE.ShaderMaterialParameters> = {}) {
    this.material = new THREE.ShaderMaterial({
      vertexShader: FULLSCREEN_VERT,
      fragmentShader,
      uniforms,
      depthTest: false,
      depthWrite: false,
      ...opts,
    });
    this.mesh = new THREE.Mesh(triangle, this.material);
    this.mesh.frustumCulled = false;
    this.scene.add(this.mesh);
  }

  get uniforms(): Record<string, THREE.IUniform> {
    return this.material.uniforms;
  }

  render(renderer: THREE.WebGLRenderer, target: THREE.WebGLRenderTarget | null, layer = 0): void {
    renderer.setRenderTarget(target, layer);
    renderer.render(this.scene, FullscreenPass.camera);
  }

  dispose(): void {
    this.material.dispose();
  }
}

/**
 * Hand an instanced mesh its first `n` instances for this frame: only those
 * rows are uploaded (not the whole capacity), and with none it's hidden,
 * since an empty instanced draw still costs a draw call in every pass.
 */
export function commitInstances(im: THREE.InstancedMesh, n: number, extra: readonly THREE.BufferAttribute[] = []): void {
  im.count = n;
  im.visible = n > 0;
  if (!n) return;
  const upload = (a: THREE.BufferAttribute) => {
    a.clearUpdateRanges();
    a.addUpdateRange(0, n * a.itemSize);
    a.needsUpdate = true;
  };
  upload(im.instanceMatrix);
  if (im.instanceColor) upload(im.instanceColor);
  for (const a of extra) upload(a);
}

export function makeTarget(w: number, h: number, opts: THREE.RenderTargetOptions = {}): THREE.WebGLRenderTarget {
  return new THREE.WebGLRenderTarget(w, h, {
    type: THREE.HalfFloatType,
    format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    depthBuffer: false,
    ...opts,
  });
}
